import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import API from '../api';

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function Login() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // If user is already authenticated, send them back to the app instead of showing login
    const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
    if (token) { navigate('/', { replace: true }); return; }

  }, [navigate]);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setInstallPrompt(null);
  };

  const login = async () => {
    setError('');
    // If SITE_KEY is configured, enforce captcha completion
    if (SITE_KEY && !captchaToken) {
      setError('Please complete the CAPTCHA verification.');
      return;
    }
    setLoading(true);
    try {
      const res = await API.post('/auth/login', {
        email: form.email,
        password: form.password,
        captcha_token: captchaToken || '',
      });
      const { role, name, user_id, csrf_token, access_token, force_password_change } = res.data;
      localStorage.setItem('role', role);
      localStorage.setItem('name', name);
      localStorage.setItem('user_id', String(user_id));
      localStorage.setItem('force_password_change', force_password_change ? '1' : '0');
      if (csrf_token) {
        sessionStorage.setItem('csrf_token', csrf_token);
        // Mirror CSRF to localStorage too — on iOS/Android PWA cold-start,
        // sessionStorage is wiped but localStorage usually survives, so we
        // can restore the CSRF header from here. Without this, every POST
        // after a PWA relaunch fails with 403 "CSRF token missing or invalid".
        localStorage.setItem('csrf_token', csrf_token);
      }
      if (access_token) {
        sessionStorage.setItem('access_token', access_token);
        // Mirror to localStorage for iOS PWA session restore (cleared on app close).
        // Safe: CSRF middleware exempts Bearer-token requests, so no CSRF risk.
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('access_token_at', String(Date.now()));
      }
      navigate('/', { replace: true });
    } catch (err) {
      // Rate limit must NOT read as "wrong password" — that misdiagnosis has
      // caused unnecessary password resets for users whose credentials were fine.
      if (err.response?.status === 429) {
        setError('Too many login attempts from your network. Please wait 1–2 minutes and try again — your password is probably fine.');
        recaptchaRef.current?.reset();
        setCaptchaToken('');
        return;
      }
      const detail = err.response?.data?.detail || 'Login failed. Check credentials.';
      const isCaptchaErr = detail.toLowerCase().includes('captcha');
      setError(isCaptchaErr ? 'CAPTCHA expired or invalid — please tick the checkbox again.' : detail);
      recaptchaRef.current?.reset();
      setCaptchaToken('');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') login(); };

  return (
    <div className="login-page">
      <div className="login-box">
        <div className="login-logo">
          <img src="/logo.png" alt="Sapience Edu Connect" style={{ height:72, marginBottom:12, objectFit:'contain' }} />
          <h1 style={{ fontSize:22, fontWeight:800, color:'#0f1f45', letterSpacing:-0.3, marginBottom:4 }}>Sapience Edu Connect</h1>
          <p style={{ fontSize:12.5, color:'#64748b', fontWeight:500 }}>Connecting minds shaping futures</p>
          <p style={{ fontSize:12, color:'#94a3b8', marginTop:4 }}>Sign in to continue</p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="form-group">
          <label>Email Address</label>
          <input
            className="form-control"
            type="email"
            placeholder="you@example.com"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            onKeyDown={handleKey}
          />
        </div>
        <div className="form-group">
          <label>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              className="form-control"
              type={showPwd ? 'text' : 'password'}
              placeholder="••••••••"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              onKeyDown={handleKey}
              style={{ paddingRight: 44 }}
            />
            <button
              type="button"
              onClick={() => setShowPwd(p => !p)}
              style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#64748b', fontSize:16, lineHeight:1, padding:4 }}
              tabIndex={-1}
            >
              {showPwd ? '🙈' : '👁️'}
            </button>
          </div>
        </div>

        {SITE_KEY && (
          <div style={{ margin:'16px 0', display:'flex', justifyContent:'center' }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={SITE_KEY}
              onChange={token => setCaptchaToken(token || '')}
              onExpired={() => setCaptchaToken('')}
            />
          </div>
        )}

        <button className="btn btn-primary" onClick={login} disabled={loading || (SITE_KEY && !captchaToken)}>
          {loading ? 'Signing in…' : 'Sign In →'}
        </button>

        {installPrompt && !installed && (
          <button
            onClick={handleInstall}
            style={{
              marginTop: 12,
              width: '100%',
              padding: '11px 0',
              borderRadius: 10,
              border: '2px solid #1e3a8a',
              background: 'transparent',
              color: '#1e3a8a',
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            📲 Install App
          </button>
        )}
        {installed && (
          <p style={{ textAlign:'center', fontSize:12, color:'#16a34a', marginTop:10, fontWeight:600 }}>
            App installed successfully!
          </p>
        )}
      </div>
    </div>
  );
}
